import { Loading3QuartersOutlined } from '@ant-design/icons';
import { Radio, Switch, Tooltip } from 'antd';
import { Key } from 'antd/lib/table/interface';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { TreeShaped } from 'domains/appDomain';
import {
  JobsDistributionWayEnum,
  NetworkPESwitchEnum,
  UpdateNetworkMember,
} from 'domains/networkDomain';
import {
  isIndustryAvgOrg,
  Organization,
  isCustomerOrg,
} from 'domains/orgDomain';
import { useUser } from 'hooks';
import { useOrg } from 'hooks/useOrg';
import { keyBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listToTree } from 'utils/misc';

type MemberOrganization = Organization & {
  isInNetwork: boolean;
  peswitch?: string;
  _id?: number;
  jobsDistributionWay?: string;
  displayOnJobEstimator?: boolean;
  excludeInJobConfig?: boolean;
  disableAddToCartInJobConfig?: boolean;
};

export const NetworkMemberTable = ({
  searchValue,
  addMember,
  orgs,
  onManageClick,
}: {
  searchValue: string;
  addMember: boolean;
  orgs: Organization[];
  onManageClick: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.loading.effects.network.getNetworksByMemberId
  );
  const orgsLoading = useAppSelector(
    (state) => state.loading.effects.org.getOrgs
  );
  const members = useAppSelector((state) => state.network.members);
  const contextOrg = useOrg();
  const user = useUser();
  const pageSize = useAppSelector((state) => state.network.pageSize);
  const currentPage = useAppSelector((state) => state.network.currentPage);
  const [firstLoading, setFirstLoading] = useState<boolean>(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);
  const [tableFilters, setTableFilters] = useState({
    isInNetwork: addMember ? ['active', 'inactive'] : ['active'],
  });
  const memberHitMap = keyBy(members, 'memberId');

  const filteredOrgs = useMemo(() => {
    // if (contextOrg && !isPlatformOrg(contextOrg)) {
    //   return [];
    // }
    return orgs
      .filter((org) => {
        const isSearchHit = searchValue
          ? String(org.id) === searchValue ||
            org.name.toLowerCase().includes(searchValue.toLowerCase())
          : true;

        const networkStatusFilter = tableFilters.isInNetwork;
        let isStatusPass = false;

        let isMember = Boolean(memberHitMap[org.id]);

        if (networkStatusFilter) {
          if (
            networkStatusFilter.length === 0 ||
            networkStatusFilter.length === 2
          ) {
            isStatusPass = true;
          } else if (networkStatusFilter[0] === 'active' && isMember) {
            isStatusPass = true;
          } else if (networkStatusFilter[0] === 'inactive' && !isMember) {
            isStatusPass = true;
          }
        } else {
          isStatusPass = true;
        }

        return isSearchHit && org.id !== contextOrg?.id && isStatusPass;
      })
      .map((org) => {
        const member = members.find((item) => item.memberId === org.id);
        const isInNetwork = Boolean(memberHitMap[org.id]);
        return {
          ...org,
          isInNetwork,
          peswitch: member?.peswitch,
          _id: member?.id,
          jobsDistributionWay: member?.jobsDistributionWay,
          displayOnJobEstimator: isInNetwork
            ? member?.displayOnJobEstimator ?? true
            : false,
          // PRD-471: unset means the admin default - both switches on, so Industry Average is
          // excluded from job config and, once that is turned off, Add to Cart stays disabled.
          excludeInJobConfig: member?.excludeInJobConfig ?? true,
          disableAddToCartInJobConfig:
            member?.disableAddToCartInJobConfig ?? true,
        };
      });
  }, [contextOrg, memberHitMap, orgs, searchValue, tableFilters, members]);

  const treeData: TreeShaped<MemberOrganization>[] = useMemo(() => {
    return listToTree<MemberOrganization>(filteredOrgs);
  }, [filteredOrgs]);

  const [firstRender, setFirstRender] = useState<boolean>(true);
  useEffect(() => {
    if (!loading && !orgsLoading && treeData.length > 0 && firstRender) {
      setFirstRender(false);
      let expandedKeys: Key[] = [];
      treeData.forEach((item) => {
        if (item.children && item.children.length > 0) {
          expandedKeys.push(item.id);
        }
      });

      setExpandedRowKeys(expandedKeys);
    }
  }, [
    expandedRowKeys,
    expandedRowKeys.length,
    firstRender,
    loading,
    orgsLoading,
    treeData,
    treeData.length,
  ]);

  useEffect(() => {
    setTableFilters({
      isInNetwork: addMember ? ['active', 'inactive'] : ['active'],
    });
    setFirstRender(true);
    dispatch.network.setState({
      currentPage: 1,
    });
  }, [addMember, dispatch.network]);

  useEffect(() => {
    async function fetchMembers() {
      if (contextOrg) {
        setFirstLoading(true);
        await dispatch.network.getNetworksByMemberId({
          networkId: contextOrg.id,
        });
        setFirstLoading(false);
      }
    }

    fetchMembers();
  }, [contextOrg, dispatch.network]);

  const onChangeInNetwork = useCallback(
    async (
      checkedValue: boolean | JobsDistributionWayEnum,
      org,
      isMemberManage: boolean = true
    ) => {
      try {
        if (isMemberManage) {
          if (checkedValue) {
            await dispatch.network.addMemeber({
              memberId: org.id,
              networkId: contextOrg?.id,
              displayOnJobEstimator: true,
              excludeInJobConfig: true,
              disableAddToCartInJobConfig: true,
            });
          } else {
            await dispatch.network.deleteMember(memberHitMap[org.id]);
          }
        } else {
          await dispatch.network.updateMember({
            memberId: org.id,
            networkId: contextOrg?.id,
            jobsDistributionWay:
              typeof checkedValue === 'boolean' ? undefined : checkedValue,
            id: org._id,
          });
        }
      } catch (e) {}
      await dispatch.network.getNetworksByMemberId({
        networkId: contextOrg?.id,
      });
    },
    [dispatch.network, memberHitMap, contextOrg?.id]
  );

  /**
   * PRD-471: persist one of the Industry Average job config controls and refresh the table.
   */
  const updateJobConfigControl = useCallback(
    async (
      record: TreeShaped<MemberOrganization>,
      changes: Partial<UpdateNetworkMember>
    ) => {
      try {
        await dispatch.network.updateMember({
          memberId: record.id,
          networkId: contextOrg?.id,
          id: record._id,
          ...changes,
        });
      } catch (e) {}
      await dispatch.network.getNetworksByMemberId({
        networkId: contextOrg?.id,
      });
    },
    [contextOrg?.id, dispatch.network]
  );

  /**
   * PRD-471: the two job config controls only exist for the Industry Average org, and only while it
   * is part of the network. They render below its name instead of taking two whole columns.
   */
  const jobConfigControlsRender = (record: TreeShaped<MemberOrganization>) => {
    if (!isIndustryAvgOrg(record) || !record.isInNetwork) {
      return null;
    }
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          marginTop: 8,
          whiteSpace: 'nowrap',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {t('platform.network.excludeInJobConfig')}
          <Switch
            checked={Boolean(record.excludeInJobConfig)}
            disabled={!addMember}
            onClick={(c, e) => e.stopPropagation()}
            onChange={(checked) =>
              updateJobConfigControl(record, { excludeInJobConfig: checked })
            }
          />
        </span>
        {!record.excludeInJobConfig && (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            {t('platform.network.disableAddToCartForIndustryAverage')}
            <Switch
              checked={Boolean(record.disableAddToCartInJobConfig)}
              disabled={!addMember}
              onClick={(c, e) => e.stopPropagation()}
              onChange={(checked) =>
                updateJobConfigControl(record, {
                  disableAddToCartInJobConfig: checked,
                })
              }
            />
          </span>
        )}
      </div>
    );
  };

  const pricingDataColumns: TableColumnsType<TreeShaped<MemberOrganization>> = [
    {
      title: t('platform.network.pricingData'),
      dataIndex: 'peswitch',
      render: (value, record) => {
        return value === NetworkPESwitchEnum.LIVE ? (
          <>{t('common.live')}</>
        ) : value === NetworkPESwitchEnum.PRIVATE ? (
          <>{t('common.private')}</>
        ) : (
          <></>
        );
      },
    },
  ];

  const memberColumns: TableColumnsType<TreeShaped<MemberOrganization>> =
    addMember
      ? [
          {
            title: t('platform.network.isInNetwork'),
            dataIndex: 'isInNetwork',
            width: 160,
            filteredValue: tableFilters.isInNetwork,
            filters: ['active', 'inactive'].map((s) => {
              let status;
              if (s === 'active') {
                status = t('platform.network.active');
              } else {
                status = t('platform.network.inactive');
              }

              return {
                text: status,
                value: s,
              };
            }),

            render: (value, record) => {
              return firstLoading ? (
                <Loading3QuartersOutlined />
              ) : (
                <Switch
                  checked={value}
                  onClick={(c, e) => e.stopPropagation()}
                  onChange={(checked) => onChangeInNetwork(checked, record)}
                />
              );
            },
          },
        ]
      : [];

  const showPoOrRfmColumn = contextOrg?.sendJobsToRfm;
  const poOrRfmColumns: TableColumnsType<TreeShaped<MemberOrganization>> =
    showPoOrRfmColumn
      ? [
          {
            title: t('platform.network.poOrRfm'),
            dataIndex: 'jobsDistributionWay',
            render: (value, record) => {
              if (!value || isIndustryAvgOrg(record)) return <></>;
              return (
                <>
                  {addMember ? (
                    <Radio.Group
                      defaultValue={value}
                      onChange={(checked) =>
                        onChangeInNetwork(checked.target.value, record, false)
                      }
                    >
                      <Radio value={JobsDistributionWayEnum.PO}>PO</Radio>
                      <Radio value={JobsDistributionWayEnum.RFM}>RFM</Radio>
                    </Radio.Group>
                  ) : (
                    <>{value}</>
                  )}
                </>
              );
            },
          },
        ]
      : [];
  const manageMarketplaceNetworksColumn: TableColumnsType<
    TreeShaped<MemberOrganization>
  > =
    !addMember && user?.orgId === 1 && contextOrg?.id === 1
      ? [
          {
            title: 'Marketplace Networks',
            render: (_, record) => {
              const isCustomer = isCustomerOrg(record);
              const button = (
                <RevxButton
                  type="link"
                  size="small"
                  disabled={isCustomer}
                  onClick={() => {
                    if (!isCustomer) {
                      dispatch.network.setState({
                        marketplaceNetworksForOrgId: record.id,
                      });
                      onManageClick();
                    }
                  }}
                >
                  Manage
                </RevxButton>
              );

              return isCustomer ? (
                <Tooltip title="Customer Org is not supporting network configuration">
                  <span>{button}</span>
                </Tooltip>
              ) : (
                button
              );
            },
          },
        ]
      : [];

  const displayOnJobEstimatorColumns: TableColumnsType<
    TreeShaped<MemberOrganization>
  > = [
    {
      title: 'Display on Job Estimator Page',
      dataIndex: 'displayOnJobEstimator',
      width: 160,
      render: (value, record) => {
        if (!record.isInNetwork) return <></>;
        return (
          <Switch
            checked={!!value}
            disabled={!addMember}
            onClick={(c, e) => e.stopPropagation()}
            onChange={async (checked) => {
              try {
                await dispatch.network.updateMember({
                  memberId: record.id,
                  networkId: contextOrg?.id,
                  id: record._id,
                  displayOnJobEstimator: checked,
                });
              } catch (e) {}
              await dispatch.network.getNetworksByMemberId({
                networkId: contextOrg?.id,
              });
            }}
          />
        );
      },
    },
  ];

  const columns: TableColumnsType<TreeShaped<MemberOrganization>> = [
    {
      title: undefined,
      dataIndex: 'id',
      align: 'right',
      width: 40,
      render: () => {
        return '';
      },
    },
    {
      title: t('common.id'),
      width: 100,
      dataIndex: 'id',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      render: (value, record) => {
        const jobConfigControls = jobConfigControlsRender(record);
        if (!jobConfigControls) {
          return <>{value}</>;
        }
        return (
          <>
            <div>{value}</div>
            {jobConfigControls}
          </>
        );
      },
    },

    {
      title: t('platform.org.table.columns.tags'),
      dataIndex: 'tags',
    },
    {
      title: t('platform.org.table.columns.city'),
      dataIndex: 'city',
    },
  ];

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          if (pagination.pageSize !== pageSize) {
            dispatch.network.setState({
              pageSize: pagination.pageSize,
              currentPage: 1,
            });
            dispatch.network.setState({ pageSize: pagination.pageSize });
          }

          if (pagination.current !== currentPage) {
            dispatch.network.setState({
              currentPage: pagination.current,
            });
          }
          break;
        case 'filter':
          setTableFilters(filters);
          break;
      }
    },
    [currentPage, dispatch.network, pageSize]
  );

  return (
    <RevxTable<MemberOrganization>
      loading={loading || orgsLoading}
      columns={[
        ...columns,
        ...memberColumns,
        ...pricingDataColumns,
        ...poOrRfmColumns,
        ...manageMarketplaceNetworksColumn,
        ...displayOnJobEstimatorColumns,
      ]}
      pagination={{
        pageSize: pageSize,
        current: currentPage,
        showTotal: (total) =>
          t('common.pagerTotal', { total: filteredOrgs.length }),
      }}
      indentSize={20}
      dataSource={treeData}
      expandable={{
        expandRowByClick: true,
        expandIconColumnIndex: 0,
        expandedRowKeys: expandedRowKeys,
        onExpandedRowsChange: (rows) => {
          setExpandedRowKeys([...rows]);
        },
      }}
      rowClassName={(record: TreeShaped<MemberOrganization>) => {
        let cls = '';
        if (record.children) {
          cls += ' expandable';
        }
        if (expandedRowKeys.includes(record.id) && record.children) {
          cls += ' expanded';
        } else if (
          record.parentId &&
          filteredOrgs.find((item) => item.id === record.parentId)
        ) {
          cls += ' nested';
        }
        return cls;
      }}
      onChange={onTableChange}
    />
  );
};
